import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar, Plus, Lock, Unlock, Edit2, RefreshCw, CheckCircle, XCircle, AlertTriangle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "../../../components/ui/alert-dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TENANT_ID = "cao";

const Ejercicios = () => {
  const [loading, setLoading] = useState(true);
  const [ejercicios, setEjercicios] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedEjercicio, setSelectedEjercicio] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, ejercicio: null });

  const [form, setForm] = useState({
    nombre: "",
    fecha_inicio: "",
    fecha_fin: ""
  });

  const fetchEjercicios = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/ejercicios`);
      setEjercicios(response.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error al cargar ejercicios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEjercicios();
  }, []);

  const handleOpenModal = (ejercicio = null) => {
    if (ejercicio) {
      setEditMode(true);
      setSelectedEjercicio(ejercicio);
      setForm({
        nombre: ejercicio.nombre,
        fecha_inicio: ejercicio.fecha_inicio,
        fecha_fin: ejercicio.fecha_fin
      });
    } else {
      setEditMode(false);
      setSelectedEjercicio(null);
      // Sugerir siguiente año
      const currentYear = new Date().getFullYear();
      const lastEjercicio = ejercicios[0];
      const nextYear = lastEjercicio ? parseInt(lastEjercicio.nombre.match(/\d{4}/)?.[0] || currentYear) + 1 : currentYear;
      setForm({
        nombre: `Ejercicio ${nextYear}`,
        fecha_inicio: `${nextYear}-01-01`,
        fecha_fin: `${nextYear}-12-31`
      });
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.nombre || !form.fecha_inicio || !form.fecha_fin) {
      toast.error("Complete todos los campos");
      return;
    }

    if (new Date(form.fecha_inicio) >= new Date(form.fecha_fin)) {
      toast.error("La fecha de inicio debe ser anterior a la fecha de fin");
      return;
    }

    setSaving(true);
    try {
      if (editMode) {
        await axios.put(`${API_URL}/api/erp/empresas/${TENANT_ID}/ejercicios/${selectedEjercicio.id}`, form);
        toast.success("Ejercicio actualizado");
      } else {
        await axios.post(`${API_URL}/api/erp/empresas/${TENANT_ID}/ejercicios`, form);
        toast.success("Ejercicio creado");
      }
      setModalOpen(false);
      fetchEjercicios();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleCerrar = async (ejercicio) => {
    setConfirmDialog({ open: true, action: "cerrar", ejercicio });
  };

  const handleAbrir = async (ejercicio) => {
    setConfirmDialog({ open: true, action: "abrir", ejercicio });
  };

  const confirmAction = async () => {
    const { action, ejercicio } = confirmDialog;
    setConfirmDialog({ open: false, action: null, ejercicio: null });
    
    try {
      if (action === "cerrar") {
        await axios.put(`${API_URL}/api/erp/empresas/${TENANT_ID}/ejercicios/${ejercicio.id}/cerrar`);
        toast.success("Ejercicio cerrado exitosamente");
      } else {
        await axios.put(`${API_URL}/api/erp/empresas/${TENANT_ID}/ejercicios/${ejercicio.id}/abrir`);
        toast.success("Ejercicio reabierto exitosamente");
      }
      fetchEjercicios();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al realizar la acción");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-AR");
  };

  const ejercicioAbierto = ejercicios.find(e => !e.cerrado);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2853B9]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ejercicios-contables">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3876]">Ejercicios Contables</h1>
          <p className="text-gray-600">Gestión de períodos contables</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={fetchEjercicios} data-testid="btn-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => handleOpenModal()} className="bg-[#2853B9] hover:bg-[#1E3876]" data-testid="btn-nuevo-ejercicio">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Ejercicio
          </Button>
        </div>
      </div>

      {/* Alerta de ejercicio abierto */}
      {ejercicioAbierto && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-l-4 border-l-green-500 bg-green-50">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-green-800">Ejercicio activo: {ejercicioAbierto.nombre}</p>
                <p className="text-sm text-green-600">
                  Período: {formatDate(ejercicioAbierto.fecha_inicio)} - {formatDate(ejercicioAbierto.fecha_fin)}
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {!ejercicioAbierto && ejercicios.length > 0 && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-l-4 border-l-yellow-500 bg-yellow-50">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <p className="font-medium text-yellow-800">
                No hay ejercicio abierto. Debe abrir un ejercicio para registrar asientos.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Tabla de ejercicios */}
      <Card className="cao-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Listado de Ejercicios
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Fecha Inicio</TableHead>
                <TableHead>Fecha Fin</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Fecha Cierre</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ejercicios.map((ej) => (
                <TableRow key={ej.id} data-testid={`ejercicio-row-${ej.id}`}>
                  <TableCell className="font-medium">{ej.nombre}</TableCell>
                  <TableCell>{formatDate(ej.fecha_inicio)}</TableCell>
                  <TableCell>{formatDate(ej.fecha_fin)}</TableCell>
                  <TableCell>
                    {ej.cerrado ? (
                      <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                        <Lock className="w-3 h-3 mr-1" />
                        Cerrado
                      </Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700">
                        <Unlock className="w-3 h-3 mr-1" />
                        Abierto
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{ej.fecha_cierre ? formatDate(ej.fecha_cierre) : "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenModal(ej)}
                        data-testid={`btn-editar-${ej.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      {ej.cerrado ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAbrir(ej)}
                          className="text-green-600 hover:text-green-700"
                          data-testid={`btn-abrir-${ej.id}`}
                        >
                          <Unlock className="w-4 h-4 mr-1" />
                          Abrir
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCerrar(ej)}
                          className="text-orange-600 hover:text-orange-700"
                          data-testid={`btn-cerrar-${ej.id}`}
                        >
                          <Lock className="w-4 h-4 mr-1" />
                          Cerrar
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {ejercicios.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No hay ejercicios creados. Cree el primer ejercicio para comenzar.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal crear/editar */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editMode ? "Editar Ejercicio" : "Nuevo Ejercicio"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre del ejercicio</Label>
              <Input
                id="nombre"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej: Ejercicio 2026"
                data-testid="input-nombre"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fecha_inicio">Fecha de inicio</Label>
                <Input
                  id="fecha_inicio"
                  type="date"
                  value={form.fecha_inicio}
                  onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })}
                  data-testid="input-fecha-inicio"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fecha_fin">Fecha de fin</Label>
                <Input
                  id="fecha_fin"
                  type="date"
                  value={form.fecha_fin}
                  onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })}
                  data-testid="input-fecha-fin"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#2853B9] hover:bg-[#1E3876]">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación cerrar/abrir */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, action: null, ejercicio: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog.action === "cerrar" ? "¿Cerrar ejercicio?" : "¿Reabrir ejercicio?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.action === "cerrar" ? (
                <>
                  Al cerrar el ejercicio <strong>{confirmDialog.ejercicio?.nombre}</strong>, no se podrán registrar más asientos en este período.
                  Esta acción se puede revertir posteriormente.
                </>
              ) : (
                <>
                  Al reabrir el ejercicio <strong>{confirmDialog.ejercicio?.nombre}</strong>, podrá volver a registrar asientos.
                  Solo puede haber un ejercicio abierto a la vez.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAction} className={confirmDialog.action === "cerrar" ? "bg-orange-600" : "bg-green-600"}>
              {confirmDialog.action === "cerrar" ? "Cerrar Ejercicio" : "Abrir Ejercicio"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Ejercicios;
